/**
 * ECDSA signature encoding conversion between ASN.1 DER (r, s INTEGER) and the
 * fixed-width raw format used by WebCrypto.
 *
 * @module
 */

import { integer, readSequenceChildren, sequence } from '#micro509/internal/asn1/der';

/** Convert a DER-encoded ECDSA signature (SEQUENCE of two INTEGERs) to fixed-width raw `r || s`. */
export function derEcdsaSignatureToRaw(signature: Uint8Array, partLength: number): Uint8Array {
	const parts = readSequenceChildren(signature);
	const r = parts[0];
	const s = parts[1];
	if (r === undefined || s === undefined || parts.length !== 2) {
		throw new Error('Malformed ECDSA DER signature');
	}
	if (r.tag !== 0x02 || s.tag !== 0x02) {
		throw new Error('ECDSA DER signature must contain two INTEGERs');
	}
	return concatFixedWidth(trimLeadingZero(r.value), trimLeadingZero(s.value), partLength);
}

/** Convert a fixed-width raw `r || s` ECDSA signature to DER-encoded SEQUENCE of two INTEGERs. */
export function rawEcdsaSignatureToDer(signature: Uint8Array, partLength: number): Uint8Array {
	if (signature.length !== partLength * 2) {
		throw new Error('Unexpected ECDSA raw signature length');
	}
	return sequence([integer(signature.slice(0, partLength)), integer(signature.slice(partLength))]);
}

/**
 * Normalize a WebCrypto ECDSA signature to DER.
 *
 * Spec-compliant WebCrypto always returns fixed-width raw `r || s`
 * (exactly `rawSignatureBytes` long), which needs conversion. Detection is
 * by length, never by sniffing the first byte: a raw signature whose `r`
 * happens to start with `0x30` (1-in-256 per signature) is
 * indistinguishable from DER by tag, and embedding it unconverted produces
 * a certificate/CRL that OpenSSL rejects with a signature failure.
 */
export function ecdsaSignatureToDer(signature: Uint8Array, rawSignatureBytes: number): Uint8Array {
	if (signature.length === rawSignatureBytes) {
		return rawEcdsaSignatureToDer(signature, rawSignatureBytes / 2);
	}
	return signature;
}

/**
 * Try the opposite ECDSA encoding: DER→raw or raw→DER.
 *
 * Returns `undefined` if conversion fails, letting callers retry verification
 * with the alternate encoding.
 */
export function alternateEcdsaSignatureEncoding(
	signature: Uint8Array,
	partLength: number,
): Uint8Array | undefined {
	// Try DER→raw first if it looks like DER (starts with SEQUENCE tag)
	if (signature[0] === 0x30) {
		try {
			return derEcdsaSignatureToRaw(signature, partLength);
		} catch {
			// Fall through to try raw→DER
		}
	}
	// Try raw→DER
	try {
		return rawEcdsaSignatureToDer(signature, partLength);
	} catch {
		return undefined;
	}
}

/** Left-pad and concatenate `r` and `s` into a fixed-width `partLength * 2` byte buffer. */
export function concatFixedWidth(
	left: Uint8Array,
	right: Uint8Array,
	partLength: number,
): Uint8Array {
	if (left.length > partLength || right.length > partLength) {
		throw new Error('ECDSA signature integer too large');
	}
	const out = new Uint8Array(partLength * 2);
	out.set(left, partLength - left.length);
	out.set(right, out.length - right.length);
	return out;
}

/** Strip leading zero bytes from a DER INTEGER value, keeping at least one byte. */
function trimLeadingZero(bytes: Uint8Array): Uint8Array {
	let index = 0;
	while (index < bytes.length - 1 && bytes[index] === 0) {
		index += 1;
	}
	return bytes.slice(index);
}
